import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Files, Upload } from '@factorialco/f0-react/icons/app'
import { PagesFoldersListDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { usePagesContext } from 'modules/pages/lib/hooks/usePagesContext'

interface FoldersTableProps {
  setOpenModal: (open: boolean) => void
}
export const FoldersTable = ({ setOpenModal }: FoldersTableProps) => {
  const { navigation } = useNavigation()
  const { canEditPages } = usePagesContext()

  const source = useGraphqlDataCollectionSource({
    document: PagesFoldersListDocument,
    mapData: (data) => data.handbookPages.foldersConnection.nodes,
    totalItemSummary: (totalCount) =>
      totalCount === 1
        ? i18n.t('pages.folders_table_headers.total_folder')
        : i18n.t('pages.folders_table_headers.total_folders', { count: totalCount }),
    mapPagination: (data) => ({
      total: data.handbookPages.foldersConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search, sortings }) => {
      return {
        search: search ?? '',
        sort: sortings?.[0],
        onlyFoldersWithItems: !canEditPages,
      }
    },
    itemUrl: ({ slug }) => navigation.pages.folder.fullPath({ folderSlug: slug }),
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('pages.folders_table_headers.name'),
        sortBy: 'name',
      },
      numberOfItems: {
        label: i18n.t('pages.folders_table_headers.total_items_title'),
        sortBy: 'numberOfItems',
      },
    },
    defaultSortings: {
      field: 'name',
      order: 'desc',
    },
    secondaryActions: canEditPages
      ? () => [
          {
            label: i18n.t('pages.folders_table_headers.import_pdf'),
            icon: Upload,
            onClick: () => setOpenModal(true),
          },
        ]
      : undefined,
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item) => item.name,
            description: (item) => item.description ?? '',
            avatar: () => ({
              type: 'emoji',
              emoji: '📁',
            }),
            cardProperties: [
              {
                label: i18n.t('pages.folders_table_headers.total_items_title'),
                icon: Files,
                render: (item) => ({
                  type: 'text',
                  value:
                    item.numberOfItems === 1
                      ? i18n.t('pages.folders_table_headers.total_item')
                      : i18n.t('pages.folders_table_headers.total_items', {
                          count: item.numberOfItems,
                        }),
                }),
              },
            ],
          },
        },
        {
          type: 'list',
          options: {
            itemDefinition: (item) => ({
              title: item.name,
              avatar: {
                type: 'emoji',
                emoji: '📁',
              },
              description: [item.description].filter(Boolean),
            }),
            fields: [
              {
                label: i18n.t('pages.folders_table_headers.total_items_title'),
                render: (item) => ({
                  type: 'tag',
                  value: {
                    label:
                      item.numberOfItems === 1
                        ? i18n.t('pages.folders_table_headers.total_item')
                        : i18n.t('pages.folders_table_headers.total_items', {
                            count: item.numberOfItems,
                          }),
                  },
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}
